import api from "./api";

/**
 * Submits a referral payload to Ascella for a given opportunity ID.
 * @param {string|number} opportunityId - The ID of the opportunity.
 * @param {Object} payload - The referral data structure.
 * @returns {Promise<Object|null>} The API response data or null if the request failed.
 */
export const submitToAscella = async (opportunityId, payload = {}) => {
  try {
    const response = await api.put(
      `/api/ascella/referrals/submit/${opportunityId}`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("submitToAscella failed:", error);
    return null;
  }
};

/**
 * Fetches the pre-populated referral payload for a specific member program.
 * @param {number|string} memberProgramId - The internal unique identifier for the Member Program.
 * @returns {Promise<Object>} The ReferralRequest object.
 */
export const prepareAscellaReferral = async (memberProgramId) => {
  try {
    const response = await api.get(
      `/api/ascella/referrals/prepare/${memberProgramId}`,
    );
    return response.data;
  } catch (error) {
    console.error("prepareAscellaReferral failed:", error);
    throw error;
  }
};

/**
 * Submits the referral data for a specific member program.
 * @param {number|string} memberProgramId - The internal unique identifier for the Member Program.
 * @param {Object} referralRequest - The ReferralRequest object payload.
 * @returns {Promise<Object>} The ReferralResponse containing the external ID.
 */
export const submitAscellaReferral = async (
  memberProgramId,
  referralRequest,
) => {
  try {
    const response = await api.put(
      `/api/ascella/referrals/submit/${memberProgramId}`,
      referralRequest,
      {
        headers: { "Content-Type": "application/json" },
      },
    );
    return response.data;
  } catch (error) {
    console.error("submitAscellaReferral failed:", error);
    throw error;
  }
};

/**
 * Tracks the Ascella orders for a given member and opportunity.
 * @param {string|number} memberId - The ID of the member.
 * @param {string|number} opportunityId - The ID of the opportunity.
 * @returns {Promise<Object>} The tracking details.
 */
export const trackAscellaOrder = async (memberId, opportunityId) => {
  try {
    const response = await api.get(
      `/outreach/members/${memberId}/opportunities/${opportunityId}/tracking?vendorName=ASCELLA`
    );
    return response.data;
  } catch (error) {
    console.error("trackAscellaOrder failed:", error);
    throw error;
  }
};

/**
 * Fetches the submitted referral record from Ascella.
 * @param {number|string} referralId - The ID of the submitted referral.
 * @returns {Promise<Object>} The submitted referral details.
 */
export const getSubmittedReferral = async (referralId) => {
  try {
    const response = await api.get(
      `/api/ascella/referrals/submitted/${referralId}`
    );
    return response.data;
  } catch (error) {
    console.error("getSubmittedReferral failed:", error);
    throw error;
  }
};

